/**
 * Neo → Legacy adapter
 *
 * Converts Neo backend socket events into the GameAction format
 * expected by useGameQueue / gameReducer so that Game.tsx can
 * render Neo games with the exact same UI as Java legacy games.
 */

import type { GameAction, DeclaredPoignee } from '../game/gameActions';
import { PoigneeType, DELAYS } from '../game/gameActions';
import type { CardNumber } from '../types/game';

// ============================================
// Neo event payload types (mirrors backend)
// ============================================

export interface NeoStatePayload {
  tableId: string;
  config: {
    playerCount: number;
    petitesAutorisees: boolean;
    iaPeutPrendre: boolean;
    commentaire: string;
    iaType?: string;
    adminPseudo: string;
  };
  players: Array<{
    pseudo: string;
    position: number;
    isBot: boolean;
    isConnected: boolean;
    ready: boolean;
    cardsInHand: number;
  }>;
  phase: string;
  dealNumber: number;
  dealer: number;
  currentPlayer: number;
  hand: CardNumber[];
  bids: Array<{ player: number; bid: number }>;
  highestBid: number;
  taker: number | null;
  calledKing: CardNumber | null;
  partner: number | null;
  chien: CardNumber[] | null;
  currentTrick: { cards: CardNumber[]; players: number[] };
  numPli: number;
  scores: NeoDealScore | null;
  cumulativeScores: number[];
  declaredPoignees: Array<{ player: number; type: number; cards: CardNumber[] }>;
}

interface NeoDealScore {
  takerPoints: number;
  defensePoints: number;
  oudlers: number;
  requiredPoints: number;
  difference: number;
  contract: number;
  multiplier: number;
  petitAuBout: number;
  poigneeBonus: number;
  slamBonus: number;
  baseScore: number;
  playerScores: number[];
  won: boolean;
}

// ============================================
// Phase mapping (Neo string → legacy number)
// ============================================

/** Neo phase string → legacy frontend phase number */
const NEO_PHASE_TO_LEGACY: Record<string, number> = {
  waiting: 0,
  bidding: 2,
  king_call: 3,
  chien: 4,
  ecart: 5,
  playing: 6,
  end_of_deal: 99999,
  finished: 99999,
};

/**
 * Neo phase string → legacy server 'etat' value (used in distribue message).
 * etat: 0=nothing, 1=bidding, 2=allPassed, 3=kingCall, 4=ecart, 5=playing, 6=finished
 */
const NEO_PHASE_TO_ETAT: Record<string, number> = {
  bidding: 1,
  king_call: 3,
  chien: 4,
  ecart: 4,
  playing: 5,
  end_of_deal: 6,
  finished: 6,
};

// ============================================
// Conversion functions
// ============================================

/**
 * Convert a neo:state (full sync) into a sequence of legacy actions
 * that rebuild the game state for the reducer.
 */
export function neoStateToActions(
  state: NeoStatePayload,
  myPseudo: string,
): GameAction[] {
  const actions: GameAction[] = [];
  const myPlayer = state.players.find(
    p => p.pseudo.toLowerCase() === myPseudo.toLowerCase(),
  );
  const myPosition = myPlayer?.position ?? 0;

  // 1. Set the player's position
  actions.push({ type: 'set_position', position: myPosition });

  // 2. Set up player info
  actions.push({
    type: 'infos_table',
    data: {
      pseudos: state.players
        .sort((a, b) => a.position - b.position)
        .map(p => p.pseudo),
      commentaire: state.config.commentaire,
      position: myPosition,
      connectes: state.players
        .sort((a, b) => a.position - b.position)
        .map(p => (p.isBot ? '' : (p.isConnected ? '1' : '0'))),
    },
  });

  // 3. Set up player list
  actions.push({
    type: 'joueurs',
    data: {
      joueurs: [...state.players]
        .sort((a, b) => a.position - b.position)
        .map(p => ({
          pseudo: p.pseudo,
          position: p.position,
          connecte: p.isConnected,
        })),
    },
  });

  // 4. If a deal is active (not waiting), reconstruct the deal state
  const phase = NEO_PHASE_TO_LEGACY[state.phase] ?? 0;
  const etat = NEO_PHASE_TO_ETAT[state.phase] ?? 0;
  if (phase > 0 && state.hand.length > 0) {
    // Build contrats array from bids
    const contrats = new Array(state.config.playerCount).fill(-1);
    for (const bid of state.bids) {
      contrats[bid.player] = bid.bid;
    }

    actions.push({
      type: 'distribue',
      data: {
        donneur: state.dealer,
        cartes: state.hand,
        contrats,
        etat,
        numPli: state.numPli,
        pliEnCours: state.currentTrick.cards,
        entame: state.currentTrick.players[0],
        chien: state.chien ?? undefined,
        contratObligatoire: state.config.petitesAutorisees ? -1 : 7,
        syncCurrentPlayer: state.currentPlayer,
        trickPlayerPositions: state.currentTrick.players,
      },
    });

    // If there's a taker determined, set it via contrat action
    if (state.taker !== null && state.highestBid > 0) {
      actions.push({
        type: 'contrat',
        data: {
          joueur: state.taker,
          contrat: state.highestBid,
        },
      });
    }

    // If scores are available (end of deal), push score action
    if (state.scores && phase === 99999) {
      actions.push({
        type: 'score',
        data: neoScoreToLegacy(state.scores, state.cumulativeScores),
      });
    }
  } else if (state.phase === 'waiting') {
    actions.push({
      type: 'neo_waiting_lobby',
      data: {
        players: state.players.map(p => ({
          pseudo: p.pseudo,
          position: p.position,
          isBot: p.isBot,
          isConnected: p.isConnected,
        })),
        adminPseudo: state.config.adminPseudo,
      },
    });
  }

  return actions;
}

/**
 * Convert neo:deal event into legacy distribue action.
 *
 * Legacy etat values: 0=nothing, 1=bidding, 2=allPassed, 3=kingCall, 4=ecart, 5=playing, 6=finished
 */
export function neoDealToActions(data: {
  hand: CardNumber[];
  dealer: number;
  playerCount: number;
  dealNumber: number;
  firstBidder: number;
}, petitesAutorisees: boolean): GameAction[] {
  return [{
    type: 'distribue',
    data: {
      donneur: data.dealer,
      cartes: data.hand,
      contrats: new Array(data.playerCount).fill(-1),
      etat: 0, // 0 = new deal, starts bidding
      numPli: 0,
      contratObligatoire: petitesAutorisees ? -1 : 7,
    },
  }];
}

/**
 * Convert neo:bid_made into legacy contrat actions.
 */
export function neoBidToActions(data: {
  player: number;
  bid: number;
  nextPlayer: number | null;
}): GameAction[] {
  return [
    { type: 'bid_pre_delay', ms: DELAYS.BID_THINKING },
    {
      type: 'contrat',
      data: {
        joueur: data.player,
        contrat: data.bid,
      },
    },
    { type: 'bid_advance' },
  ];
}

/**
 * Convert neo:king_called into legacy appel action.
 */
export function neoKingCalledToActions(data: {
  player: number;
  king: CardNumber;
}): GameAction[] {
  // Legacy appel uses couleur (suit index 0-3), but the card number identifies the king
  // The gameReducer stores couleur as-is; for Neo we pass the actual card number
  return [{
    type: 'appel',
    data: {
      couleur: data.king,
    },
  }];
}

/**
 * Convert neo:chien into legacy chien actions.
 */
export function neoChienToActions(data: {
  cards: CardNumber[];
  taker: number;
  contract: number;
}): GameAction[] {
  return [
    {
      type: 'chien',
      data: {
        cartes: data.cards,
        preneur: data.taker,
        contrat: data.contract,
      },
    },
    { type: 'delay', ms: DELAYS.CHIEN_SHOWN },
    { type: 'chien_vu' },
  ];
}

/**
 * Convert neo:ecart_done into legacy ecart action.
 */
export function neoEcartDoneToActions(_data: {
  player: number;
}): GameAction[] {
  return [{
    type: 'ecart',
    data: {},
  }];
}

/**
 * Convert neo:hand_updated — handled directly in Game.tsx via hand_update action.
 * This function is unused; the translation happens inline.
 */

/**
 * Convert neo:card_played into legacy carte actions.
 */
export function neoCardPlayedToActions(data: {
  player: number;
  card: CardNumber;
}): GameAction[] {
  return [
    {
      type: 'carte',
      data: {
        joueur: data.player,
        carte: data.card,
      },
    },
    { type: 'delay', ms: DELAYS.CARD_PLAYED },
  ];
}

/**
 * Convert neo:poignee_declared into legacy poignee actions.
 */
export function neoPoigneeDeclaredToActions(data: {
  player: number;
  type: number;
  cards: CardNumber[];
}): GameAction[] {
  const poigneeData: DeclaredPoignee = {
    player: data.player,
    type: data.type as PoigneeType,
    cards: data.cards,
  };

  return [
    { type: 'poignee_announce', data: poigneeData },
    { type: 'delay', ms: DELAYS.POIGNEE_DISPLAY },
    { type: 'poignee_hide' },
  ];
}

/**
 * Convert neo:trick_end into legacy fin_pli actions.
 */
export function neoTrickEndToActions(data: {
  winner: number;
  cards: CardNumber[];
  players: number[];
  points: number;
}): GameAction[] {
  return [
    { type: 'delay', ms: DELAYS.TRICK_END },
    {
      type: 'fin_pli',
      data: {
        gagnant: data.winner,
        pli: data.cards,
      },
    },
  ];
}

/**
 * Convert neo:scores into legacy score action.
 */
export function neoScoresToActions(data: {
  dealScore: NeoDealScore;
  cumulativeScores: number[];
}): GameAction[] {
  return [{
    type: 'score',
    data: neoScoreToLegacy(data.dealScore, data.cumulativeScores),
  }];
}

/**
 * Convert neo:all_passed — show a brief "all passed" state before next deal.
 */
export function neoAllPassedToActions(): GameAction[] {
  return [
    {
      type: 'score',
      data: {
        contratRealise: false,
        nbBouts: 0,
        pointsAttaque: 0,
        difference: 0,
        allPassed: true,
      },
    },
  ];
}

// ============================================
// Internal helpers
// ============================================

function neoScoreToLegacy(
  score: NeoDealScore,
  cumulativeScores: number[],
): Record<string, any> {
  return {
    contrat: score.contract,
    contratRealise: score.won,
    pointsJoueur: score.takerPoints,
    difference: score.difference,
    nbBouts: score.oudlers,
    pointsAttaque: score.takerPoints,
    scores: score.playerScores,
    cumulativeScores,
    petitAuBout: score.petitAuBout,
    poigneeBonus: score.poigneeBonus,
    slamBonus: score.slamBonus,
    multiplier: score.multiplier,
    won: score.won,
  };
}
